import { useState } from 'react'
import {
  type AnnouncementCreateData,
  type AnnouncementLevel,
  type AnnouncementSummary,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'

interface AnnouncementFormModalProps {
  /** When provided, the modal is in edit mode. */
  announcement?: AnnouncementSummary
  onClose: () => void
  onSubmit: (data: AnnouncementCreateData) => Promise<void>
}

const LEVELS: { value: AnnouncementLevel; label: string }[] = [
  { value: 'info', label: 'Info (purple)' },
  { value: 'warning', label: 'Warning (amber)' },
  { value: 'critical', label: 'Critical (red)' },
]

const inputClass =
  'w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2 text-sm text-gray-900 dark:text-white focus:border-[#503864] focus:outline-none focus:ring-2 focus:ring-[#503864]/20'

/** ISO 8601 (UTC) -> value for a <input type="datetime-local"> in the admin's local time. */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** datetime-local value -> ISO 8601 (UTC), or null when empty. */
function fromLocalInput(local: string): string | null {
  if (!local) return null
  return new Date(local).toISOString()
}

export function AnnouncementFormModal({
  announcement,
  onClose,
  onSubmit,
}: AnnouncementFormModalProps) {
  const editing = Boolean(announcement)

  const [message, setMessage] = useState(announcement?.message ?? '')
  const [level, setLevel] = useState<AnnouncementLevel>(announcement?.level ?? 'info')
  const [linkUrl, setLinkUrl] = useState(announcement?.link_url ?? '')
  const [linkLabel, setLinkLabel] = useState(announcement?.link_label ?? '')
  const [dismissible, setDismissible] = useState(announcement?.dismissible ?? true)
  const [isActive, setIsActive] = useState(announcement?.is_active ?? true)
  const [startsAt, setStartsAt] = useState(toLocalInput(announcement?.starts_at))
  const [endsAt, setEndsAt] = useState(toLocalInput(announcement?.ends_at))

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Message is required')
      return
    }
    const start = fromLocalInput(startsAt)
    const end = fromLocalInput(endsAt)
    if (start && end && new Date(end) < new Date(start)) {
      setError('End time must be after start time')
      return
    }
    setLoading(true)
    setError(null)
    try {
      await onSubmit({
        message: message.trim(),
        level,
        link_url: linkUrl.trim() || null,
        link_label: linkLabel.trim() || null,
        dismissible,
        is_active: isActive,
        starts_at: start,
        ends_at: end,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save announcement')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-white">
            {editing ? 'Edit Announcement' : 'New Announcement'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="space-y-4">
          {/* Message */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Message <span className="text-gray-400">({message.length}/280)</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 280))}
              placeholder="Shown site-wide across the web app. Keep it short; it renders on one line."
              rows={2}
              className={inputClass}
            />
          </div>

          {/* Level */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Level
            </label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value as AnnouncementLevel)}
              className={inputClass}
            >
              {LEVELS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>

          {/* Optional CTA link */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <CustomTextInput
                label="Link URL (optional)"
                placeholder="https://..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            </div>
            <div>
              <CustomTextInput
                label="Link label (optional)"
                placeholder="Learn more"
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
              />
            </div>
          </div>

          {/* Optional schedule window */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Starts at (optional)
              </label>
              <input
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Ends at (optional)
              </label>
              <input
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          <p className="text-xs text-gray-400">
            Leave the schedule empty to show immediately and until manually deactivated.
          </p>

          {/* Toggles */}
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[#503864] focus:ring-[#503864]"
              />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={dismissible}
                onChange={(e) => setDismissible(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[#503864] focus:ring-[#503864]"
              />
              Dismissible
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <CustomButton variant="secondary" onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton variant="primary" onClick={handleSubmit} loading={loading}>
            {editing ? 'Save Changes' : 'Publish'}
          </CustomButton>
        </div>
      </div>
    </div>
  )
}
